import React, { useState, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import './SilenceExperimentPage.css';

interface SentenceData {
  index: number;
  text: string;
  voice: string;
  audioUrl: string;
}

interface VersionResult {
  audioUrl: string;
  duration: number;
  silencePercent: number;
}

interface ExperimentResult {
  original: VersionResult;
  trimmed: VersionResult;
  preprocessed: VersionResult;
  both: VersionResult;
}

const EXPERIMENT_SENTENCES: SentenceData[] = [
  {
    index: 2,
    text: 'Yes, of course. Here you are.',
    voice: 'Evan',
    audioUrl: '/uploads/audio/draft_1713e123-bb3f-487a-8078-6ada28e9b849_1772544422243/sentence_002.mp3',
  },
  {
    index: 9,
    text: 'Good! I have a nut allergy.',
    voice: 'Sarah',
    audioUrl: '/uploads/audio/draft_1713e123-bb3f-487a-8078-6ada28e9b849_1772544422243/sentence_009.mp3',
  },
  {
    index: 16,
    text: 'Okay, great. Can I pay now?',
    voice: 'Sarah',
    audioUrl: '/uploads/audio/draft_1713e123-bb3f-487a-8078-6ada28e9b849_1772544422243/sentence_016.mp3',
  },
  {
    index: 19,
    text: 'Perfect. Here is your check.',
    voice: 'Evan',
    audioUrl: '/uploads/audio/draft_1713e123-bb3f-487a-8078-6ada28e9b849_1772544422243/sentence_019.mp3',
  },
];

const VERSION_LABELS: { key: keyof ExperimentResult; label: string; color: string }[] = [
  { key: 'original', label: 'Original', color: '#6b7280' },
  { key: 'trimmed', label: 'Silence Trim', color: '#3b82f6' },
  { key: 'preprocessed', label: 'Text Preprocess', color: '#8b5cf6' },
  { key: 'both', label: 'Both', color: '#059669' },
];

const SilenceExperimentPage: React.FC = () => {
  const [results, setResults] = useState<Record<number, ExperimentResult>>({});
  const [generating, setGenerating] = useState<Record<number, boolean>>({});
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const playAudio = useCallback((url: string, key: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingAudio === key) {
      setPlayingAudio(null);
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const audio = new Audio(fullUrl);
    audioRef.current = audio;
    setPlayingAudio(key);
    audio.onended = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.onerror = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.play().catch(() => { setPlayingAudio(null); audioRef.current = null; });
  }, [playingAudio]);

  const generateExperiment = useCallback(async (sentence: SentenceData) => {
    setGenerating(prev => ({ ...prev, [sentence.index]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/dictionary/tts-compare/silence-experiment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: sentence.text,
          voiceId: sentence.voice,
          originalAudioUrl: sentence.audioUrl,
        }),
      });
      const data = await res.json();
      if (data.error) {
        console.error('Experiment error:', data.error);
        return;
      }
      setResults(prev => ({ ...prev, [sentence.index]: data }));
    } catch (err) {
      console.error('Failed to run experiment:', err);
    } finally {
      setGenerating(prev => ({ ...prev, [sentence.index]: false }));
    }
  }, []);

  const generateAll = useCallback(async () => {
    for (const sentence of EXPERIMENT_SENTENCES) {
      if (!results[sentence.index]) {
        await generateExperiment(sentence);
      }
    }
  }, [results, generateExperiment]);

  const isAnyGenerating = Object.values(generating).some(Boolean);

  return (
    <div className="silence-exp-page">
      <div className="silence-exp-header">
        <h1>TTS Silence Experiment</h1>
        <p className="silence-exp-desc">
          A/B comparison of 4 processing methods on Inworld TTS sentences with silence issues.
          Course: <code>88a81927</code> (Dining Out: Conversation)
        </p>
        <button
          className="silence-exp-btn silence-exp-btn--primary"
          onClick={generateAll}
          disabled={isAnyGenerating}
        >
          {isAnyGenerating ? 'Generating...' : 'Generate All'}
        </button>
      </div>

      <div className="silence-exp-cards">
        {EXPERIMENT_SENTENCES.map(sentence => {
          const result = results[sentence.index];
          const isGen = generating[sentence.index];

          return (
            <div key={sentence.index} className="silence-exp-card">
              <div className="silence-exp-card-header">
                <span className="silence-exp-card-index">#{sentence.index}</span>
                <span className="silence-exp-card-text">"{sentence.text}"</span>
                <span className={`silence-exp-badge silence-exp-badge--${sentence.voice.toLowerCase()}`}>
                  {sentence.voice}
                </span>
              </div>

              {!result && (
                <div className="silence-exp-card-empty">
                  <button
                    className="silence-exp-btn silence-exp-btn--generate"
                    onClick={() => generateExperiment(sentence)}
                    disabled={isGen || isAnyGenerating}
                  >
                    {isGen ? 'Generating...' : 'Generate Comparison'}
                  </button>
                </div>
              )}

              {result && (
                <div className="silence-exp-versions">
                  {VERSION_LABELS.map(({ key, label, color }) => {
                    const ver = result[key];
                    const audioKey = `${sentence.index}-${key}`;
                    const isPlaying = playingAudio === audioKey;
                    return (
                      <div key={key} className="silence-exp-version-row">
                        <span
                          className="silence-exp-version-label"
                          style={{ borderLeftColor: color }}
                        >
                          {label}
                        </span>
                        <button
                          className={`silence-exp-play-btn ${isPlaying ? 'silence-exp-play-btn--playing' : ''}`}
                          onClick={() => playAudio(ver.audioUrl, audioKey)}
                          title={`Play ${label}`}
                        >
                          {isPlaying ? (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <rect x="6" y="4" width="4" height="16" />
                              <rect x="14" y="4" width="4" height="16" />
                            </svg>
                          ) : (
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </button>
                        <span className="silence-exp-duration">{ver.duration.toFixed(2)}s</span>
                        <span className={`silence-exp-silence-badge ${ver.silencePercent > 20 ? 'silence-exp-silence-badge--high' : ver.silencePercent > 10 ? 'silence-exp-silence-badge--med' : 'silence-exp-silence-badge--low'}`}>
                          {ver.silencePercent}% silence
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {result && result.original.duration > 0 && (
                <div className="silence-exp-card-summary">
                  <span>
                    Saved: <strong>{(result.original.duration - result.both.duration).toFixed(2)}s</strong>
                    {' '}({Math.round((1 - result.both.duration / result.original.duration) * 100)}% shorter)
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SilenceExperimentPage;
